import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useLocaleProvider } from "../../components/locale-provider";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { useUserAuthInfo } from "../../lib/auth";
import { currentDate } from "../../lib/dateUtils";
import { useApiClient } from "../../lib/http";
import { formatMessage, formatNumber } from "../../lib/intlUtils";
import CrudList, { defaultListMessages } from "../shared/crud/list";
import {
  generateReportCsv,
  generateReportHeaders,
  generateReportRequest,
  useReportFilters,
} from "./application/report.service";
import { ReportEntity } from "./domain/report.entity";
import { usePropertyCurrencies, useReportApi } from "./infrastructure/report.api";
import { reportFilterMessages, reportMessages, reportTooltipMessages } from "./messages";

export enum ReportType {
  DEFAULT = "DEFAULT",
  CONCEPT = "CONCEPT",
  STATUS = "STATUS",
}

export interface ReportCell {
  id: number;
  period: string;
  property: string;
  group: string;
  partner: string;
  avgStay: string;
  avgAdvance: string;
  totalReservations: string;
  totalAmount: string;
  avgAmount: string;
  totalRoomsNight: string;
  avgRoomsNight: string;
  avgOccupantNight: string;
  currency: string;
  status: string;
  concept: string;
}

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZES = [100, 500, 1000];
const VALID_RAW_STATUS = ["reserva", "prereserva", "cancelada"];

function formatReportCells({
  report,
  defaultCurrency,
  filterCurrency,
  filterStatus,
  locale,
}: {
  report: ReportEntity[];
  defaultCurrency: string;
  filterCurrency?: string;
  filterStatus?: string;
  locale: string;
}): ReportCell[] {
  const formatOpts = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  const currency = filterCurrency ?? defaultCurrency;
  const status = filterStatus ?? "confirmed";
  return report.map(it => ({
    ...it,
    status:
      VALID_RAW_STATUS.includes(it.status) && reportFilterMessages[status]
        ? formatMessage(reportFilterMessages[status], locale)
        : it.status,
    currency: currency === "original" ? defaultCurrency : currency,
    avgStay: formatNumber(it.avgStay, locale, formatOpts),
    avgAdvance: formatNumber(it.avgAdvance, locale, formatOpts),
    totalReservations: formatNumber(it.totalReservations, locale, formatOpts),
    totalAmount: formatNumber(it.totalAmount, locale, formatOpts),
    avgAmount: formatNumber(it.avgAmount, locale, formatOpts),
    totalRoomsNight: formatNumber(it.totalRoomsNight, locale, formatOpts),
    avgRoomsNight: formatNumber(it.avgRoomsNight, locale, formatOpts),
    avgOccupantNight: formatNumber(it.avgOccupantNight, locale, formatOpts),
  })) as ReportCell[];
}

function Report({ type }: { type: ReportType }) {
  const userAuthInfo = useUserAuthInfo();
  const intl = useIntl();
  const { currentLocale } = useLocaleProvider();
  const apiClient = useApiClient();
  const { getBookingsReport } = useReportApi(apiClient);
  const propertyTickers = useUserPropertyTickers();
  const filters = useReportFilters(type);
  const { defaultCurrency } = usePropertyCurrencies();
  const [groupByEntity, setGroupByEntity] = useState<string | undefined>();
  const [totalResults, setTotalResults] = useState<number>(0);

  const headers = useMemo(
    () => generateReportHeaders(type, !!userAuthInfo?.isWitAdmin, groupByEntity),
    [groupByEntity, userAuthInfo?.isWitAdmin, type]
  );

  const fetchBookingsReport = useCallback(
    async ({ currentPage, filterValues, pageSize, setTotalRowsCount }) => {
      if (!defaultCurrency || !currentLocale) {
        return;
      }

      setGroupByEntity(filterValues?.groupByEntity);
      const request = generateReportRequest({
        propertyTickers,
        currentPage,
        filterValues,
        pageSize,
        type,
        defaultCurrency,
      });
      const response = await getBookingsReport({
        ...request,
        type,
        sumText: intl.formatMessage(reportMessages.totalSum),
        avgText: intl.formatMessage(reportMessages.average),
      });
      setTotalResults(response.totalResults);
      if (typeof setTotalRowsCount === "function") {
        setTotalRowsCount(response.totalResults);
      }
      return formatReportCells({
        defaultCurrency,
        filterCurrency: filterValues?.currency,
        filterStatus: filterValues?.status,
        locale: currentLocale,
        report: response.report,
      });
    },
    [getBookingsReport, propertyTickers, intl, defaultCurrency, currentLocale, type]
  );

  const onExport = useCallback(
    async ({ filterValues }) => {
      if (!totalResults || !currentLocale) {
        return;
      }

      const results = await fetchBookingsReport({
        currentPage: -1,
        filterValues,
        pageSize: totalResults,
        setTotalRowsCount: undefined,
      });
      if (!results) {
        return;
      }

      const link = document.createElement("a");
      link.href = generateReportCsv(
        results,
        currentLocale,
        type,
        !!userAuthInfo?.isWitAdmin,
        groupByEntity
      );
      link.download = `report_${format(currentDate(), "yyyyMMdd")}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [
      fetchBookingsReport,
      totalResults,
      currentLocale,
      groupByEntity,
      type,
      userAuthInfo?.isWitAdmin,
    ]
  );

  if (!filters || !Object.keys(filters).length || !defaultCurrency) {
    return null;
  }

  return (
    <CrudList<ReportCell, {}>
      messages={{
        ...defaultListMessages,
        entityName: reportMessages[`entityName_${type}`],
        tableHeaders: reportMessages,
        tooltipHeaders: reportTooltipMessages,
      }}
      columns={headers}
      onGetAll={fetchBookingsReport}
      onExport={onExport}
      paging
      pageSizes={PAGE_SIZES}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      filters={filters}
      managerName={`bookingsReport_${type}`}
      filtersClosedByDefault={false}
      firstLoad={false}
    />
  );
}

export default withAuthorizationVerifier(Report);
